if (!customElements.get('variant-selects')) {
  const harnessSizeOrder = ['S-M', 'L-XL', '2XL', '3XL'];
  const harnessSizeFit = {
    'S-M': 'Weight: 130 - 170 lbs  •  Height: 4’ 10” - 5’ 9”',
    'L-XL': 'Weight: 160 - 225 lbs  •  Height: 4’ 10” - 6’ 3”',
    '2XL': 'Weight: 180 - 275 lbs  •  Height: 5’ 2” - 6’ 8”',
    '3XL': 'Weight: 250 - 310 lbs  •  Height: 5’ 6” - 6’ 8”',
  };

  customElements.define(
    'variant-selects',
    class VariantSelects extends HTMLElement {
      constructor() {
        super();
        this.lastHarnessSizeIndex = null;
      }

      get selectedOptionValues() {
        return Array.from(
          this.querySelectorAll('select option[selected], fieldset input:checked')
        ).map(({ dataset }) => dataset.optionValueId);
      }

      getInputForEventTarget(target) {
        return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
      }

      connectedCallback() {
        this.addEventListener('change', (event) => {
          const target = this.getInputForEventTarget(event.target);
          this.updateSelectedSwatchValue(target);
          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange, {
            data: {
              event,
              target,
              selectedOptionValues: this.selectedOptionValues,
            },
          });
        });

        requestAnimationFrame(() => this.initializeHarnessOverlay());
      }

      isHarnessSizeOption(optionName = '') {
        const normalizedName = optionName.toLowerCase().replace(/[^a-z]/g, '');
        return normalizedName === 'harnesssize' || normalizedName === 'selectharnesssize';
      }

      getProductGallery() {
        const product = this.closest('.product');
        return product?.querySelector('.product__media-gallery-viewer') || null;
      }

      updateFormattedOptionValue(element, value = '') {
        if (!element) return;

        const parts = String(value).split('®');
        if (parts.length === 1) {
          element.textContent = value;
          return;
        }

        const fragment = document.createDocumentFragment();
        parts.forEach((part, index) => {
          if (index > 0) {
            const symbol = document.createElement('sup');
            symbol.className = 'trademark-symbol';
            symbol.textContent = '®';
            fragment.appendChild(symbol);
          }
          fragment.appendChild(document.createTextNode(part));
        });
        element.replaceChildren(fragment);
      }

      ensureHarnessOverlayContainer() {
        const gallery = this.getProductGallery();
        if (!gallery) return null;

        let container = gallery.querySelector('.harness-overlay-container');
        if (!container) {
          container = document.createElement('div');
          container.className = 'harness-overlay-container';
          container.setAttribute('aria-live', 'polite');
          gallery.appendChild(container);
        }

        return container;
      }

      updateHarnessOverlay(selectedSize, description) {
        const gallery = this.getProductGallery();
        const container = this.ensureHarnessOverlayContainer();
        if (!gallery || !container) return;

        if (!selectedSize || !description) {
          container.replaceChildren();
          gallery.classList.remove('has-size-overlay');
          this.lastHarnessSizeIndex = null;
          return;
        }

        gallery.classList.add('has-size-overlay');

        const current = container.querySelector('.harness-overlay:last-child');
        const next = document.createElement('div');
        const sizeLabel = document.createElement('span');
        const fitText = document.createElement('span');

        next.className = 'harness-overlay';
        sizeLabel.className = 'size-label';
        sizeLabel.textContent = `Size: ${selectedSize}`;
        fitText.textContent = `Fits: ${description}`;
        next.append(sizeLabel, fitText);

        const newIndex = harnessSizeOrder.indexOf(selectedSize);
        const hasDirection = this.lastHarnessSizeIndex !== null && newIndex !== -1;
        const direction = hasDirection && newIndex !== this.lastHarnessSizeIndex
          ? (newIndex > this.lastHarnessSizeIndex ? -1 : 1)
          : 0;
        this.lastHarnessSizeIndex = newIndex === -1 ? null : newIndex;

        next.style.transform = `translateX(${direction * 100}%)`;
        container.appendChild(next);

        // Force layout before transitioning between size descriptions.
        void next.offsetWidth;
        next.style.transition = 'transform 0.4s ease';
        next.style.transform = 'translateX(0)';

        if (current && current !== next) {
          current.style.transition = 'transform 0.4s ease';
          current.style.transform = `translateX(${-direction * 100}%)`;
          current.addEventListener('transitionend', () => current.remove(), { once: true });
        }
      }

      initializeHarnessOverlay() {
        const harnessFieldset = Array.from(this.querySelectorAll('fieldset[data-option-name]')).find((fieldset) =>
          this.isHarnessSizeOption(fieldset.dataset.optionName)
        );
        const checkedInput = harnessFieldset?.querySelector('input:checked');
        if (checkedInput) this.updateSelectedSwatchValue(checkedInput);
      }

      updateSelectedSwatchValue(target) {
        if (!target) return;

        const { value, dataset, tagName, type } = target;
        const fieldset = target.closest('fieldset[data-swatch-type], div[data-swatch-type]');
        if (!fieldset) return;

        const swatchType = fieldset.dataset.swatchType;
        const selectedValueEl = fieldset.querySelector('[data-selected-swatch-value]');

        // ---- Variant Image Swatches ----
        if (swatchType === 'variant_image' && tagName === 'INPUT' && type === 'radio') {
          this.updateFormattedOptionValue(selectedValueEl, value);
        }

        // ---- Color Swatches ----
        if (swatchType === 'color') {
          // fallback to value if dataset.optionSwatchValue is missing
          const swatchValue = dataset.optionSwatchValue || value;

          this.updateFormattedOptionValue(selectedValueEl, value);

          // update visual swatch circle
          const swatchCircle = fieldset.querySelector(
            '.swatch-color, .swatch, input[type="radio"]'
          );
          if (swatchCircle) {
            // FIX: match CSS variable name
            swatchCircle.style.setProperty('--swatch-background', swatchValue);
            swatchCircle.classList.remove('swatch--unavailable');
            swatchCircle.style.setProperty(
              '--swatch-focal-point',
              dataset.optionSwatchFocalPoint || 'unset'
            );
          }
        }

        // ---- Dropdown Swatches ----
        if (tagName === 'SELECT' && selectedValueEl) {
          this.updateFormattedOptionValue(selectedValueEl, value);
        }

        if (this.isHarnessSizeOption(fieldset.dataset.optionName)) {
          const sizeName = target.nextElementSibling?.dataset.harnessSizeName || '';
          const displayValue = sizeName ? `${value}: ${sizeName}` : value;
          this.updateFormattedOptionValue(selectedValueEl, displayValue);
          this.updateHarnessOverlay(value, harnessSizeFit[value] || '');
        }
      }
    }
  );
}
